<template>
  <div class="rights-tree-node">
    <div class="rights-tree-node__row" :style="{ paddingLeft: (level - 1) * 18 + 'px' }">
      <div class="rights-tree-node__left-action">
        <v-btn
          v-if="hasVisibleChildren"
          class="btn-open-block custom-check"
          variant="text"
          text
          :title="'Shift+клик — раскрыть всю ветку'"
          @click="handleToggleClick"
        >
          <v-icon>{{ isOpen ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
        </v-btn>
      </div>

      <div class="rights-tree-node__title">
        <b @click="$emit('go-to', node, chain)">{{ node.title }}</b>
      </div>

      <div class="rights-tree-node__rights">
        <v-checkbox
          v-for="btn in rightsButtons"
          :key="'right_' + node.id + '_' + btn.id"
          class="rights-table__vcb"
          :color="btn.color"
          :title="rightTitle(btn)"
          hide-details
          :ripple="false"
          :input-value="getRight(node, btn.title)"
          :indeterminate="!getRight(node, btn.title) && hasDescendantWithRight(btn.title)"
          indeterminate-icon="mdi-minus-box"
          @click.native.stop.prevent="$emit('toggle-right', node, btn, collectDescendants(node))"
        />
      </div>
    </div>

    <div v-show="isOpen">
      <RightsTreeNode
        v-for="child in visibleChildren"
        :key="child.id"
        :node="child"
        :level="level + 1"
        :chain="[...chain, node]"
        :open-ids="openIds"
        :rights-buttons="rightsButtons"
        :get-right="getRight"
        @toggle-open="$emit('toggle-open', $event)"
        @toggle-open-deep="(ids, val) => $emit('toggle-open-deep', ids, val)"
        @toggle-right="(n, b, d) => $emit('toggle-right', n, b, d)"
        @go-to="(n, c) => $emit('go-to', n, c)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "RightsTreeNode",
  components: {
    RightsTreeNode: () => import("./RightsTreeNode.vue"),
  },
  props: {
    node: { type: Object, required: true },
    level: { type: Number, default: 1 },
    chain: { type: Array, default: () => [] },
    openIds: { type: Array, required: true },
    rightsButtons: { type: Array, required: true },
    getRight: { type: Function, required: true },
  },
  computed: {
    isOpen() {
      return this.openIds.includes(this.node.id);
    },
    visibleChildren() {
      return (this.node.children || []).filter(
        (c) => c.type !== "task" && c.type !== "question"
      );
    },
    hasVisibleChildren() {
      return this.visibleChildren.length > 0;
    },
  },
  methods: {
    handleToggleClick(event) {
      if (event && event.shiftKey) {
        this.$emit("toggle-open-deep", this.collectSubtreeIds(this.node), !this.isOpen);
        return;
      }
      this.$emit("toggle-open", this.node.id);
    },
    // есть ли хотя бы один потомок (любой глубины, включая скрытых/нераскрытых), у которого включено это право —
    // используется для полупрозрачного (indeterminate) чекбокса родителя, чтобы не потерять несогласованность
    // между уровнями при заполнении прав
    hasDescendantWithRight(rightKey) {
      const walk = (n) => {
        const children = (n.children || []).filter(
          (c) => c.type !== "task" && c.type !== "question"
        );
        for (const child of children) {
          if (this.getRight(child, rightKey)) return true;
          if (walk(child)) return true;
        }
        return false;
      };
      return walk(this.node);
    },
    rightTitle(btn) {
      if (!this.getRight(this.node, btn.title) && this.hasDescendantWithRight(btn.title)) {
        return btn.description + " — включено не у всех дочерних элементов";
      }
      return btn.description;
    },
    // все узлы поддерева (любой глубины, включая скрытых/нераскрытых), кроме самого node —
    // передаются вместе с toggle-right, чтобы состояние права можно было каскадировать вниз при клике на родителя
    collectDescendants(node) {
      const result = [];
      const walk = (n) => {
        const children = (n.children || []).filter(
          (c) => c.type !== "task" && c.type !== "question"
        );
        for (const child of children) {
          result.push(child);
          walk(child);
        }
      };
      walk(node);
      return result;
    },
    // все id узлов поддерева (включая сам узел), у которых есть видимые дети — чтобы раскрыть всю ветку разом
    collectSubtreeIds(node) {
      const ids = [];
      const walk = (n) => {
        const children = (n.children || []).filter(
          (c) => c.type !== "task" && c.type !== "question"
        );
        if (children.length > 0) {
          ids.push(n.id);
          children.forEach(walk);
        }
      };
      walk(node);
      return ids;
    },
  },
};
</script>

<style scoped>
.rights-tree-node__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #eee;
}
.rights-tree-node__left-action {
  min-width: 22px;
  display: flex;
  align-items: center;
}
.rights-tree-node__title {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 24px;
}
.rights-tree-node__title b {
  cursor: pointer;
  transition: 0.3s;
}
.rights-tree-node__title b:hover {
  text-decoration: underline;
}
.rights-tree-node__rights {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.rights-tree-node__rights .rights-table__vcb {
  margin: 0;
  padding: 0;
  flex: 0 0 48px;
  display: flex;
  justify-content: center;
}
.rights-tree-node__rights .v-input--selection-controls {
  margin: 0;
  padding: 0;
}
.rights-tree-node__rights .v-input--selection-controls__input {
  margin: 0;
}
.rights-tree-node__rights .v-input__slot {
  min-height: 0;
}
</style>
